import { useEffect, useState } from 'react';
import { Instagram, MailOutline, YoutubeSquare } from '@/components/icons';
import { Link } from '@tanstack/react-router';

import { LEGAL } from '../../lib/legal';

const navLinks = [
  { label: '소개', href: '/about' as const },
  { label: '이용약관', href: '/terms' as const },
  { label: '개인정보처리방침', href: '/privacy' as const },
  { label: '사이트맵', href: '/sitemap' as const },
  { label: 'RSS', href: '/rss' as const },
];

// 수집 출처 — 공식 사이트로 새 탭에서 연다.
const SOURCES = [
  { label: '기업마당', href: 'https://www.bizinfo.go.kr' },
  { label: 'K-Startup', href: 'https://www.k-startup.go.kr' },
  { label: '중소벤처기업부', href: 'https://www.mss.go.kr' },
];
// 우하단 소셜 아이콘. 채널이 없는 것은 누르면 「준비 중」 말풍선이 1.5초 떴다 사라진다.
const SOCIALS = [
  { label: '인스타그램', Icon: Instagram },
  { label: '유튜브', Icon: YoutubeSquare },
];
const iconCls =
  'flex h-9 w-9 items-center justify-center rounded-full bg-surface-muted text-text-muted transition-colors hover:bg-border';

function SocialIcons() {
  const [tip, setTip] = useState<number | null>(null);
  useEffect(() => {
    if (tip === null) return;
    const t = setTimeout(() => setTip(null), 1500);
    return () => clearTimeout(t);
  }, [tip]);
  return (
    <div className="flex items-center gap-3">
      {SOCIALS.map(({ label, Icon }, i) => (
        <span key={label} className="relative">
          <button
            type="button"
            onClick={() => setTip(i)}
            className={iconCls}
            aria-label={label}
            aria-describedby={tip === i ? `social-tip-${i}` : undefined}
          >
            <Icon className="h-4 w-4" />
          </button>
          {tip === i ? (
            <span
              id={`social-tip-${i}`}
              role="tooltip"
              className="pointer-events-none absolute bottom-full left-1/2 mb-2 -translate-x-1/2 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs text-white shadow-md"
            >
              준비 중
            </span>
          ) : null}
        </span>
      ))}
      <a href={`mailto:${LEGAL.email}`} className={iconCls} aria-label="이메일">
        <MailOutline className="h-4 w-4" />
      </a>
    </div>
  );
}

export function Footer() {
  return (
    <footer className="border-t border-border bg-surface">
      <div className="mx-auto max-w-4xl">
        {/* Navigation Links */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-border px-4 py-4 text-sm">
          {navLinks.map((link, idx) => (
            <span key={link.label} className="flex items-center gap-4">
              {idx > 0 && <span className="text-border-strong">|</span>}
              <Link to={link.href} className="text-text-muted transition-colors hover:text-text">
                {link.label}
              </Link>
            </span>
          ))}
        </div>

        {/* Brand + blurb */}
        <div className="grid gap-8 px-4 py-8 sm:grid-cols-3">
          <div className="sm:col-span-2">
            <div className="mb-4 flex flex-col leading-none">
              {/* 워드마크 — 제목이 아니라 p. h3 이면 h1→h3 로 제목 순서가 깨진다(Lighthouse heading-order). */}
              <p className="font-kirang text-3xl text-text">좋소 과수원</p>
              <p className="mt-1.5 font-kirang text-[21px] leading-none text-text-subtle">
                Zoxo Orchard
              </p>
            </div>
            <div className="space-y-1 text-xs leading-relaxed text-text-muted">
              <p className="text-sm">모든 위대한 회사는 한때 좋소였다.</p>
              {/* 이스터 에그 — 평소엔 「…되어 주리.」 마우스를 올리면 「를 틀라」가 펼쳐진다(styles.css .footer-egg). */}
              <p className="footer-tagline mt-3 text-xs text-gray-900">
                개나 소나 창업하는 모두의 창업 시대, 정부지원금 바운티헌터즈를 위한 보물 지도가 되어 주리
                <span className="footer-egg" aria-hidden="true">를 틀라</span>.
              </p>
            </div>
          </div>

          <div>
            <p className="mb-3 text-sm font-medium text-text">수집 출처</p>
            {/* 모바일은 가로 한 줄(gap-3), sm 이상은 세로 목록. */}
            <ul className="mb-4 flex flex-wrap gap-x-3 gap-y-1 text-xs text-text-muted sm:flex-col sm:gap-0 sm:space-y-1">
              {SOURCES.map((s) => (
                <li key={s.href}>
                  <a href={s.href} target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-text">
                    {s.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Copyright + Social */}
        <div className="flex flex-col items-center justify-between gap-4 border-t border-border px-4 py-6 sm:flex-row">
          <p className="text-xs text-text-subtle">
            COPYRIGHT &copy; {new Date().getFullYear()} 좋소 과수원 ALL RIGHTS RESERVED.
          </p>
          <SocialIcons />
        </div>
      </div>

      {/* Mobile nav spacing */}
      <div className="h-16 sm:hidden" />
    </footer>
  );
}
